import React, { useState, useEffect } from 'react';
import { dataMeet } from '../../data/data_meet';
import { promptData } from '../../data/data_prompts';
import './CreateInstanceForm.css';

const CreateInstanceForm = ({ 
  isOpen, 
  onClose, 
  onInstanceCreated, 
  onInstanceUpdated,
  instanceType = 'template',
  title = 'Create New Meeting',
  editMode = false,
  editData = null
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    expectedDuration: '',
    level: '',
    instanceReference: '',
    promptSetId: ''
  });
  const [levels, setLevels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [promptSets, setPromptSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingLevels, setLoadingLevels] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingPromptSets, setLoadingPromptSets] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchLevels();
      fetchPromptSets();
      if (instanceType !== 'liminal') {
        fetchTemplates();
      }
      if (editMode && editData) {
        const formDataToSet = {
          name: editData.name || '',
          description: editData.description || '',
          expectedDuration: editData.expected_duration ? (editData.expected_duration / 60).toString() : '',
          level: editData.level ? editData.level.toString() : '',
          instanceReference: editData.instance_reference ? editData.instance_reference.toString() : '',
          promptSetId: editData.prompt_set_id ? editData.prompt_set_id.toString() : ''
        };
        setFormData(formDataToSet);
      } else {
        setFormData({
          name: '',
          description: '',
          expectedDuration: '',
          level: '',
          instanceReference: '',
          promptSetId: ''
        });
      }
    }
  }, [isOpen, editMode, editData, instanceType]);

  // Set form data after templates are loaded to ensure proper selection
  useEffect(() => {
    if (editMode && editData && templates.length > 0 && !loadingTemplates) {
      setFormData(prev => ({
        ...prev,
        instanceReference: editData.instance_reference ? editData.instance_reference.toString() : ''
      }));
    }
  }, [editMode, editData, templates, loadingTemplates]);

  const fetchLevels = async () => {
    try {
      setLoadingLevels(true);
      const response = await dataMeet.getInstanceLevels();
      if (response.success) {
        setLevels(response.levels || []);
      } else {
        setError('Failed to load levels');
      }
    } catch (err) {
      setError('Error loading levels: ' + err.message);
    } finally {
      setLoadingLevels(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await dataMeet.getTemplates();
      if (response.success) {
        setTemplates(response.templates || []);
      } else {
        setError('Failed to load templates');
      }
    } catch (err) {
      setError('Error loading templates: ' + err.message);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchPromptSets = async () => {
    try {
      setLoadingPromptSets(true);
      const response = await promptData.getPromptSets();
      if (response.success) {
        setPromptSets(response.promptSets || []);
      } else {
        setError('Failed to load prompt sets');
      }
    } catch (err) {
      setError('Error loading prompt sets: ' + err.message);
    } finally {
      setLoadingPromptSets(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!formData.level) {
      setError('Level is required');
      return;
    }

    if (instanceType !== 'liminal' && !formData.instanceReference) {
      setError('Template is required');
      return;
    }

    if (!formData.promptSetId) {
      setError('Prompt Set is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (editMode && editData) {
        // Update existing instance
        console.log('🔍 Updating instance with data:', {
          instanceId: editData.instance_id,
          name: formData.name,
          description: formData.description,
          expectedDuration: parseInt(formData.expectedDuration) || 60,
          level: formData.level,
          instanceType: instanceType,
          instanceReference: formData.instanceReference,
          permissions: 'private',
          prompt_set_id: formData.promptSetId
        });
        
        const response = await dataMeet.updateInstance(
          editData.instance_id,
          formData.name,
          formData.description,
          parseInt(formData.expectedDuration) || 60,
          formData.level,
          instanceType,
          formData.instanceReference || null,
          'private', // permissions
          formData.promptSetId
        );

        if (response.success) {
          onClose();
          if (onInstanceUpdated) {
            onInstanceUpdated(response.instance);
          }
        } else {
          setError(response.message || 'Failed to update instance');
        }
      } else {
        // Create new instance
        console.log('🔍 Creating instance with data:', {
          name: formData.name,
          description: formData.description,
          expectedDuration: parseInt(formData.expectedDuration) || 60,
          level: formData.level,
          instanceType: instanceType,
          instanceReference: formData.instanceReference,
          permissions: 'private',
          prompt_set_id: formData.promptSetId
        });
        
        const response = await dataMeet.createInstance(
          formData.name,
          formData.description,
          parseInt(formData.expectedDuration) || 60,
          formData.level,
          instanceType,
          formData.instanceReference || null,
          'private', // permissions
          formData.promptSetId
        );

        if (response.success) {
          onClose();
          if (onInstanceCreated) {
            onInstanceCreated(response.instance);
          }
        } else {
          setError(response.message || 'Failed to create instance');
        }
      }
    } catch (err) {
      if (err.message === 'Unauthorized') {
        setError('Your session has expired. Redirecting to login...');
        // Redirect to login after a short delay
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        setError(`Error ${editMode ? 'updating' : 'creating'} instance: ` + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      description: '',
      expectedDuration: '',
      level: '',
      instanceReference: '',
      promptSetId: ''
    });
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="create-instance-modal-overlay">
      <div className="create-instance-modal">
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="close-button" onClick={handleCancel}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="create-instance-form">
          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}



          <div className="form-field">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter meeting name"
              required
            />
          </div>

          <div className="form-field">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter meeting description"
              rows="3"
            />
          </div>

          <div className="form-field">
            <label htmlFor="expectedDuration">Expected Duration (minutes)</label>
            <input
              type="number"
              id="expectedDuration"
              name="expectedDuration"
              value={formData.expectedDuration}
              onChange={handleInputChange}
              placeholder="60"
              min="1"
              max="90"
            />
          </div>

          <div className="form-field">
            <label htmlFor="level">Level *</label>
            {loadingLevels ? (
              <div className="loading-select">Loading levels...</div>
            ) : (
              <select
                id="level"
                name="level"
                value={formData.level}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a level</option>
                {levels.map((level, index) => {
                  const levelId = level.id ? level.id.toString() : '';
                  return (
                    <option key={level.id || `level-${index}`} value={levelId}>
                      {level.name}
                    </option>
                  );
                })}
              </select>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="promptSetId">Prompt Set *</label>
            {loadingPromptSets ? (
              <div className="loading-select">Loading prompt sets...</div>
            ) : (
              <select
                id="promptSetId"
                name="promptSetId"
                value={formData.promptSetId}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a prompt set</option>
                {promptSets.map((promptSet, index) => {
                  const promptSetId = promptSet.id ? promptSet.id.toString() : '';
                  return (
                    <option key={promptSet.id || `promptSet-${index}`} value={promptSetId}>
                      {promptSet.name}
                    </option>
                  );
                })}
              </select>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="instanceReference">
              Reference ID *
            </label>
            {instanceType === 'liminal' ? (
              <input
                type="text"
                id="instanceReference"
                name="instanceReference"
                value={formData.instanceReference}
                onChange={handleInputChange}
                placeholder="Enter Liminal meeting ID"
                required
              />
            ) : (
              <>
                {loadingTemplates ? (
                  <div className="loading-select">Loading templates...</div>
                ) : (
                  <select
                    id="instanceReference"
                    name="instanceReference"
                    value={formData.instanceReference}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a template</option>
                    {templates.map((template, index) => {
                      const templateId = template.template_id ? template.template_id.toString() : '';
                      return (
                        <option key={template.template_id || `template-${index}`} value={templateId}>
                          {template.name}
                        </option>
                      );
                    })}
                  </select>
                )}
              </>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="create-button"
              disabled={loading}
            >
              {loading ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Meeting' : 'Create Meeting')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateInstanceForm; 